import type { PhotographData } from "@qendo/profile/files";
import { useMemo, useState } from "react";
import { FlatList, type FlatListProps, StyleSheet, View, type ViewProps } from "react-native";
import { Thumbnail } from "~/components/layout/gallery/Thumbnail";
import { Only } from "~/components/permission/Only";
import { useGallery } from "~/providers/GalleryProvider";
import { setWidthOnLayout } from "~/util/layout";

export type ThumbnailGalleryProps = {
    images?: string[];
    height?: number;
    itemWidth?: number;
    expandable?: boolean;
    containerProps?: ViewProps;
} & Partial<FlatListProps<PhotographData>>;

const THUMBNAIL_GAP = 8;

export const ThumbnailGallery = ({
    images,
    containerProps,
    height = 160,
    itemWidth,
    expandable,
}: ThumbnailGalleryProps) => {
    const { findMedia } = useGallery();
    const [width, setWidth] = useState(300);
    const onLayout = setWidthOnLayout(setWidth, containerProps?.onLayout);
    const items = useMemo(() => (images ?? []).map((p) => findMedia(p)).filter((i) => !!i), [images, findMedia]);
    const has_multiple = items.length > 1;

    const item_width = useMemo(() => {
        const calculatedWidth = Math.floor(has_multiple ? width - (THUMBNAIL_GAP + Math.floor(width / 6)) : width);

        return Math.abs(itemWidth ?? calculatedWidth);
    }, [has_multiple, width, itemWidth]);

    return (
        <Only
            when={items.length}
            show={() => (
                <View {...containerProps} onLayout={onLayout} style={[{ height }, containerProps?.style]}>
                    <FlatList<PhotographData>
                        horizontal
                        data={items}
                        keyExtractor={(item) => item.id}
                        scrollEventThrottle={50}
                        renderItem={({ item }) => (
                            <Thumbnail
                                image={item}
                                width={item_width}
                                height={height}
                                style={styles.thumbnail}
                                imageStyle={styles.thumbnail}
                                expandable={expandable}
                            />
                        )}
                        ItemSeparatorComponent={() => <View style={styles.separator} />}
                    />
                </View>
            )}
        />
    );
};

const styles = StyleSheet.create({
    separator: {
        width: THUMBNAIL_GAP,
    },
    thumbnail: {
        borderRadius: 8,
    },
});
